import React, { useEffect, useState } from "react";

import {
  List,
  DataTable,
  ColumnsButton,
  TextField,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  useRecordContext,
  Show,
  TabbedShowLayout,
  Tab,
  Button,
  useNotify,
  useRefresh,
  Pagination,
  Edit,
  SimpleForm,
  SelectInput,
  TextInput,
  TopToolbar,
  EditButton,
  BooleanField,
  FilterButton,
  BooleanInput,
  Link,
  ReferenceOneField,
  ReferenceManyField,
  NumberField,
  useDataProvider,
  useInput,
  FunctionField,
  Create,
  CreateButton,
  Labeled,
  SimpleShowLayout,
} from "react-admin";
import { httpClient } from "../lib/httpClient";
import { hasScope, SCOPE } from "../lib/auth";
import { Button as MuiButton } from "@mui/material";
import { ClearCheckpoint } from "../lib/fermentAutomation/clear_checkpoint";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { TableHead, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { CustomDatetimeField, CustomDateField } from "../lib/datetime";
import {
  TemperaturePlot,
  RedoxPlot,
  DensityPlot,
  SpargePlot,
  PumpoverPlot,
  AnalysisTypePlot,
} from "../charts/ferment";
import type { DataPoint } from "../charts/ferment";
import { FermentExplorer } from "../charts/fermentExplorer";
import { DetailsButton } from "../lib/detailsButton";
import { useDeviceHiddenColumns } from "../lib/useDeviceHiddenColumns";
import { bwColours } from "../lib/theme";
import { FermentAutomationStandalone } from "../lib/fermentAutomation";
import { DensityField } from "../lib/density";
import { FermentAutomationStepsMiniField } from "../lib/fermentAutomation/mini";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { SmartTankControls } from "./smart_tanks";
import { custInfo } from "../lib/customer";
import { useLocation } from "react-router-dom";

import { ShowButton, useGetList, useGetOne } from "react-admin";
import BubblesIcon from "@mui/icons-material/BubbleChart";

// The ferment (if any) that already exists for the given lot-vessel
// association. Ferments and lot-vessel associations share an ID.
export const useLotVesselFerment = (lotVesselID?: number) =>
  useGetOne("ferments", { id: lotVesselID }, { enabled: !!lotVesselID });

// Links to an existing ferment for a lot-vessel association.
export const ViewFermentButton = ({ fermentID }: { fermentID: number }) => (
  <ShowButton
    label="View Ferment"
    resource="ferments"
    record={{ id: fermentID }}
    icon={<BubblesIcon />}
    target="_blank"
  />
);

// The currently open (end_time IS NULL) lot-vessel association for a vessel.
// The database guarantees at most one open association per vessel, so the
// result has at most one record.
export const useVesselCurrentLot = (vesselID?: number) => {
  const { data, isPending, error } = useGetList(
    "lot_barrel",
    {
      pagination: { page: 1, perPage: 1 },
      filter: {
        barrelID: vesselID,
        isOpenAssocs: true,
      },
    },
    { enabled: !!vesselID },
  );

  return {
    association: data?.[0],
    isPending,
    error,
  };
};

// A lot-vessel association by ID.
export const useLotVessel = (lotVesselID?: number) =>
  useGetOne("lot_barrel", { id: lotVesselID }, { enabled: !!lotVesselID });

// The smart tank attached to a vessel, if any. Ferments record the device
// that was attached when they started, so the Create Ferment page looks
// this up when building the record.
export const useVesselSmartTank = (vesselID?: number) => {
  const { data, isPending, error } = useGetList(
    "smart_tanks",
    {
      pagination: { page: 1, perPage: 1 },
      filter: {
        vesselID: vesselID,
      },
    },
    { enabled: !!vesselID },
  );

  return {
    data: data?.[0],
    isPending,
    error,
  };
};

const ActiveCheckpointButton = () => {
  const rec = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!rec?.automation?.activeCheckpoint) return null;

  const description = rec.automation.activeCheckpoint;

  const handleClear = () => {
    httpClient(
      `/api/v2/c/${rec.customerID}/ferments/${rec.id}/clear_checkpoint`,
      {
        method: "POST",
        body: JSON.stringify({ description }),
      },
    )
      .then((resp) => {
        if (resp.ok) {
          notify("Checkpoint cleared", { type: "success" });
          refresh();
        } else {
          notify("Failed to clear checkpoint", { type: "error" });
        }
      })
      .catch((error) => {
        notify("Failed to clear checkpoint", { type: "error" });
        console.error("Clear checkpoint error:", error);
      });
  };

  return (
    <Box
      style={{
        padding: 2,
        color: bwColours.accent.pink,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      Blocked on checkpoint:
      <ClearCheckpoint
        checkpointDescription={description}
        onClear={handleClear}
        buttonLabel={description}
        variant="contained"
        color="secondary"
        buttonStyle={{ marginLeft: 12 }}
      />
    </Box>
  );
};

const FermentListActions = () => (
  <TopToolbar>
    {hasScope(SCOPE.MANAGER) ? <CreateButton /> : null}
    <FilterButton />
    <ColumnsButton />
  </TopToolbar>
);

const FermentColumn = ({ label: _label }: { label?: string }) => {
  const rec = useRecordContext();

  return (
    <div
      style={{ display: "flex", flexDirection: "column", whiteSpace: "nowrap" }}
    >
      <TextField
        source="vesselName"
        style={{ marginBottom: 3, opacity: 0.65 }}
      />
      <TextField
        source="lotName"
        style={{ fontWeight: "bold", marginBottom: 3 }}
      />
      <div
        style={{
          fontSize: "0.875rem",
          opacity: 0.65,
          lineHeight: 1.43,
          marginBottom: 3,
        }}
      >
        <span style={{}}>Started </span>
        <CustomDateField source="startTime" label="Started" />
      </div>
      {rec.endTime ? (
        <div style={{ fontSize: "0.875rem", opacity: 0.65, lineHeight: 1.43 }}>
          <span>Ended </span>
          <CustomDateField source="endTime" label="Ended" />
        </div>
      ) : (
        <div
          style={{
            fontSize: "0.875rem",
            opacity: 0.65,
            lineHeight: 1.43,
            fontStyle: "italic",
          }}
        >
          <span>Ongoing</span>
        </div>
      )}
    </div>
  );
};

const FermentAutomationStepsMiniFieldColumn = ({
  label: _label,
}: {
  label?: string;
}) => {
  const rec = useRecordContext();

  // Ferments without an associated device have no automation to show.
  if (!rec?.deviceID) {
    return null;
  }

  return (
    <div>
      <FermentAutomationStepsMiniField />
      {rec.automation?.activeCheckpoint ? <ActiveCheckpointButton /> : null}
    </div>
  );
};

// Columns hidden by default on phones; the rest stay visible. Users can
// re-enable any of them via the Columns button (their choice then wins
// over the device default for the session). Also used by the dashboard's
// Recent Ferments list, which shows the same resource.
export const FERMENT_LIST_PHONE_HIDDEN_COLUMNS = ["temperature", "redox"];

const getRowStyle = (record, _index) => ({
  backgroundColor: record.automation.activeCheckpoint ? "#f7eceeff" : null,
});

// Backed by FermentFilter in the backend: inProgress=true keeps only
// ongoing ferments, recent=true keeps ongoing plus those finished within
// the last 60 days. Both can be combined.
const fermentFilters = [
  <SelectInput
    source="inProgress"
    label="Status"
    alwaysOn
    choices={[{ id: true, name: "In Progress" }]}
    emptyText="Show all"
  />,
];

// A <DataTable> instead of <Datagrid>: a plain table in a horizontal-scroll
// wrapper, so wide cells (mini plots) keep a usable width on phones instead
// of being compressed to nothing. The Ferment column sorts by startTime
// (the list's default sort); the plot columns are not sortable.
export const FermentList = (props) => {
  const hiddenColumns = useDeviceHiddenColumns(
    FERMENT_LIST_PHONE_HIDDEN_COLUMNS,
  );
  return (
    <List
      {...props}
      perPage={100}
      filters={fermentFilters}
      pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
      sort={{ field: "startTime", order: "DESC" }}
      storeKey={false}
      empty={false}
      actions={<FermentListActions />}
    >
      <DataTable
        rowClick={false}
        bulkActionButtons={false}
        rowSx={getRowStyle}
        hiddenColumns={hiddenColumns}
        sx={{ "& .RaDataTable-tableWrapper": { overflowX: "auto" } }}
      >
        <DataTable.Col
          source="startTime"
          label="Ferment"
          sx={{ minWidth: 150 }}
        >
          <FermentColumn />
        </DataTable.Col>
        <DataTable.Col
          source="density"
          label="Density"
          disableSort
          sx={{ minWidth: 160 }}
        >
          <DensityPlot mini={true} />
        </DataTable.Col>
        <DataTable.Col
          source="temperature"
          label="Temperature"
          disableSort
          sx={{ minWidth: 160 }}
        >
          <TemperaturePlot mini={true} />
        </DataTable.Col>
        <DataTable.Col
          source="redox"
          label="Redox"
          disableSort
          sx={{ minWidth: 160 }}
        >
          <RedoxPlot mini={true} />
        </DataTable.Col>
        <DataTable.Col source="automation" label="Automation" disableSort>
          <FermentAutomationStepsMiniFieldColumn />
        </DataTable.Col>
        <DataTable.Col>
          <DetailsButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

const FermentAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <span> {"Ferment: " + record.lotName + " in " + record.vesselName}</span>
  );
};

export const FermentEdit = (
  <Edit title={<FermentAppBarTitle />} redirect="show">
    <SimpleForm>
      <TextInput source="id" readOnly />
      <TextInput source="vesselName" readOnly />
      <TextInput source="lotName" readOnly />
      <TextInput
        source="startingDens"
        label="Starting Density (g/L)"
        type="number"
      />
      <BooleanInput
        source="startingDensOverridden"
        label="Starting Density Overridden"
        helperText="Prevents automatic recalculation when ferment data is regenerated."
      />
      <TextInput
        source="mpcTargetBrixRate"
        label="MPC Target Brix Rate (°Bx/Day)"
        type="number"
      />
      <BooleanInput source="mpcArmed" label="MPC Armed" />
    </SimpleForm>
  </Edit>
);

// Vessel picker for the Create Ferment page. Selecting a vessel looks up
// its current (open) lot-vessel association, and that association's ID is
// what gets stored in the form under `source`. Since a ferment's ID is
// exactly that association's ID, the form value is the ferment's own ID —
// the rest of the standard Create flow (dataProvider.create, notification,
// redirect) works unmodified.
const LotVesselFromVesselInput = (props: {
  source?: string;
  label?: string;
}) => {
  const { source, label } = props;
  const { field: assocField } = useInput({ source });
  const { field: vesselField } = useInput({ source: "vesselID" });
  const { association } = useVesselCurrentLot(vesselField.value);

  // Keep the form value in sync with the selected vessel's current lot.
  useEffect(() => {
    assocField.onChange(association?.id ?? null);
  }, [association?.id, assocField.onChange, assocField]);

  return (
    <ReferenceInput source="vesselID" reference="vessels">
      <AutocompleteInput
        optionText={(rec) => (rec.alias ? rec.alias : rec.barcode)}
        filterToQuery={(searchText) => ({ search: searchText })}
        label={label ?? "Vessel"}
        style={{ minWidth: 300, maxWidth: "100%" }}
      />
    </ReferenceInput>
  );
};

// Shown below the vessel input, keyed on the lot-vessel association ID
// that the input provides: the current lot and the time that lot went
// into the vessel. Warns when the vessel has no current lot, and links to
// the ferment if one already exists for the association.
const FermentCreateVesselDetails = () => {
  const { field: idField } = useInput({ source: "id" });
  const { field: vesselField } = useInput({ source: "vesselID" });
  const lotVesselID = idField.value;
  const { data: association, isPending, error } = useLotVessel(lotVesselID);
  const { data: fermentRecord } = useLotVesselFerment(lotVesselID);

  if (!vesselField.value) return null;

  return (
    <div style={{ width: "100%" }}>
      <Typography
        variant="body2"
        color="text.secondary"
        style={{ marginBottom: 4 }}
      >
        Current Lot
      </Typography>
      {!lotVesselID ? (
        <Typography variant="body2" color="warning">
          This vessel does not have a current lot. A ferment can only be created
          for a vessel with an active lot.
        </Typography>
      ) : error ? (
        <Typography variant="body2" color="error">
          Failed to load the current lot.
        </Typography>
      ) : isPending ? (
        <Typography variant="body2">Loading…</Typography>
      ) : association.endTime ? (
        <Typography variant="body2" color="warning">
          This vessel does not have a current lot. A ferment can only be created
          for a vessel with an active lot.
        </Typography>
      ) : (
        <SimpleShowLayout record={association}>
          <ReferenceField
            reference="lots"
            source="lotID"
            label="Lot Name"
            link="show"
            empty="None"
          >
            <TextField source="name" />
          </ReferenceField>

          <Labeled>
            <CustomDateField source="startTime" label="Start Time" />
          </Labeled>

          {fermentRecord ? (
            <ViewFermentButton fermentID={fermentRecord.id} />
          ) : null}
        </SimpleShowLayout>
      )}
    </div>
  );
};

export const FermentCreate = () => {
  const location = useLocation();

  return (
    // Re-key on the URL so the standard ?source= prefill (and the form it
    // initializes) is re-applied when arriving with a different vessel.
    <Create redirect="list" key={location.search}>
      <SimpleForm>
        <LotVesselFromVesselInput source="id" label="Vessel" />
        <FermentCreateVesselDetails />
      </SimpleForm>
    </Create>
  );
};

const RegenerateButton = () => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [running, setRunning] = React.useState<boolean>(false);

  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext();
  if (!record) return null;

  const recordDescription = "Ferment ID " + record.id;

  const handleOpen = () => {
    setOpen(true);
  };
  const handleCancel = () => {
    setOpen(false);
  };
  const handleRegenerate = () => {
    setRunning(true);
    httpClient(
      "/api/v2/c/" +
        record.customerID +
        "/ferments/" +
        record.id +
        "/regenerate",
      {
        method: "POST",
      },
    )
      .then(async (resp) => {
        setOpen(false);
        setRunning(false);
        if (resp.ok) {
          notify(recordDescription + " regenerated successfully", {
            type: "success",
          });
          refresh();
        } else {
          notify(recordDescription + " regenerate failed", {
            type: "error",
          });
          console.log(resp);
        }
      })
      .catch((error) => {
        setOpen(false);
        setRunning(false);
        notify(recordDescription + " regeneration failed", { type: "error" });
        console.log(error);
      });
  };

  return (
    <>
      <Button label="Regenerate" size="small" onClick={handleOpen}>
        <RotateLeftIcon />
      </Button>
      <Dialog open={open}>
        <DialogTitle>Confirm Regenerate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to regenerate <b>{recordDescription}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton autoFocus onClick={handleCancel} disabled={running}>
            Cancel
          </MuiButton>
          <MuiButton onClick={handleRegenerate} disabled={running}>
            Regenerate
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const MPCTab = (props) => {
  const record = useRecordContext();
  if (!record) return null;
  const data = record.data;

  if (!data.mpcRuns) return <Tab label="MPC" disabled={true} {...props}></Tab>;

  return (
    <Tab label="MPC" {...props}>
      <Table size="small" style={{ width: "inherit" }}>
        <TableHead>
          <TableCell>Time</TableCell>
          <TableCell>Target Rate</TableCell>
          <TableCell>Time Horizon</TableCell>
          <TableCell>New Temperature</TableCell>
          <TableCell>Predicted Rate</TableCell>
        </TableHead>
        <TableBody>
          {data.mpcRuns.map((run, i) => (
            <TableRow key={i}>
              <TableCell>{run.currentTimeHours} hrs</TableCell>
              <TableCell>{run.brixRateTarget} °Bx/day</TableCell>
              <TableCell>{run.timeHorizon} hrs</TableCell>
              {run.error ? (
                <TableCell
                  colSpan={2}
                  style={{
                    color: bwColours.primary.red,
                    fontWeight: "bold",
                  }}
                >
                  {run.error}
                </TableCell>
              ) : (
                <>
                  <TableCell>{run.temperatureResult.toFixed(2)} °C</TableCell>
                  <TableCell>{run.brixRateResult.toFixed(3)} °Bx/day</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Tab>
  );
};

const FermentShowActions = () => (
  <TopToolbar>
    <EditButton />
    <RegenerateButton />
  </TopToolbar>
);

export const FermentShow = () => (
  <Show
    title={<FermentAppBarTitle />}
    actions={hasScope(SCOPE.ADMIN) ? <FermentShowActions /> : undefined}
  >
    <FermentShowLayout />
  </Show>
);

const LotVesselLinkField = ({ label: _label }) => {
  const rec = useRecordContext();
  const lotVesselEditUri = `/c/${rec.customerID}/lot_barrel/${rec.id}`;

  if (hasScope(SCOPE.ADMIN)) {
    return (
      <Link to={lotVesselEditUri} style={{ fontSize: "0.875rem" }}>
        {rec.id}
      </Link>
    );
  } else {
    return (
      <FunctionField
        style={{ fontSize: "0.875rem" }}
        render={() => {
          return rec.id;
        }}
      ></FunctionField>
    );
  }
};

// Columns hidden by default on phones for the Analyses tab.
const ANALYSES_PHONE_HIDDEN_COLUMNS = ["method", "lotID"];

// A <DataTable> so the analyses table scrolls horizontally on phones
// instead of compressing columns to nothing.
const FermentAnalysesTable = () => {
  const hiddenColumns = useDeviceHiddenColumns(ANALYSES_PHONE_HIDDEN_COLUMNS);
  return (
    <DataTable
      rowClick={false}
      bulkActionButtons={false}
      hiddenColumns={hiddenColumns}
      sx={{ "& .RaDataTable-tableWrapper": { overflowX: "auto" } }}
    >
      <DataTable.Col source="timestamp" label="Timestamp">
        <CustomDatetimeField source="timestamp" />
      </DataTable.Col>
      <DataTable.Col source="analysisTypeID" label="Analyte">
        <ReferenceField source="analysisTypeID" reference="analysis_types">
          <TextField source="analyte" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="value" label="Value">
        <NumberField source="value" />
      </DataTable.Col>
      <DataTable.Col label="Unit" disableSort>
        <ReferenceField
          source="analysisTypeID"
          reference="analysis_types"
          link={false}
        >
          <TextField source="unit" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="method" label="Method">
        <TextField source="method" />
      </DataTable.Col>
      <DataTable.Col source="vesselID" label="Vessel">
        <AliasOrBarcodeReference
          source="vesselID"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Lot">
        <ReferenceField source="lotID" reference="lots">
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  );
};

export const FermentShowLayout = (props) => {
  const rec = useRecordContext();

  // Fetch density analyses for the reference series
  const dataProvider = useDataProvider();
  const [referenceSeries, setReferenceSeries] = useState<DataPoint[]>([]);

  const densityUnit = custInfo.customerPreferences.densityUnit || "°Bx";

  // The reference points' hours are relative to the start of the device data
  // when the ferment has any, otherwise to the start of the ferment itself
  // (ferments without a smart tank have no data start time).
  const seriesStart = rec?.data?.startTime ?? rec?.startTime;

  useEffect(() => {
    if (!rec?.id || !seriesStart) return;
    dataProvider
      .getList("analyses", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "timestamp", order: "ASC" },
        filter: { fermentID: rec.id, analyte: "Density", unit: densityUnit },
      })
      .then((resp) => {
        if (!resp?.data) return;
        const startTime = new Date(seriesStart).getTime();
        setReferenceSeries(
          resp.data
            .map((a: { timestamp: string | null; value: number }) => ({
              x: a.timestamp
                ? (new Date(a.timestamp).getTime() - startTime) / 1000 / 60 / 60
                : 0,
              y: a.value,
            }))
            .sort((a: DataPoint, b: DataPoint) => a.x - b.x),
        );
      })
      .catch((err) => console.error("Failed to fetch analyses:", err));
  }, [dataProvider, rec?.id, seriesStart, densityUnit]);

  if (!rec) return null;
  const data = rec.data;

  const showExplore =
    data.densSeries &&
    data.mvModelling &&
    data.mvModelling.body &&
    data.mvModelling.body.ModelResults &&
    data.mvModelling.body.ModelResults.DensBest;

  return (
    <TabbedShowLayout {...props}>
      <Tab label="Overview" path="">
        <DensityPlot mini={false} referenceSeries={referenceSeries} />
        <TemperaturePlot mini={false} />
        <RedoxPlot mini={false} />
        <SpargePlot mini={false} />
        <PumpoverPlot mini={false} />
        <AnalysisTypePlot />
      </Tab>

      <Tab label="Control" disabled={!rec || !!rec.endTime || !rec.deviceID}>
        <ReferenceOneField
          reference="smart_tanks"
          target="lotVesselID"
          link={false}
          empty="No Smart Tank Association"
        >
          <SmartTankControls />
        </ReferenceOneField>
      </Tab>

      <Tab
        label="Automation"
        path="automation"
        disabled={!rec || !rec.deviceID}
      >
        <FermentAutomationStandalone readonly={!hasScope(SCOPE.MANAGER)} />
      </Tab>

      {hasScope(SCOPE.TECHNICIAN) ? <MPCTab path="mpc" /> : null}

      {showExplore ? (
        <Tab label="Explore" path="explore">
          <FermentExplorer data={data} />
        </Tab>
      ) : undefined}

      <Tab label="Analyses">
        <ReferenceManyField
          reference="analyses"
          target="fermentID"
          sort={{ field: "timestamp", order: "DESC" }}
          label=""
        >
          <FermentAnalysesTable />
        </ReferenceManyField>
      </Tab>

      <Tab label="Attributes" path="attributes">
        <TextField source="id" label="ID" />
        <ReferenceOneField
          label="Smart Tank ID"
          target="lotVesselID"
          reference="smart_tanks"
          link="show"
          empty={"\u00A0"}
        >
          <TextField source="id" />
        </ReferenceOneField>
        <ReferenceField
          label="Device"
          source="deviceID"
          reference="devices"
          link={hasScope(SCOPE.TECHNICIAN) ? "edit" : false}
          empty={"\u00A0"}
        >
          <TextField source="name" />
        </ReferenceField>
        <AliasOrBarcodeReference
          source="vesselID"
          reference="vessels"
          label="Vessel"
        />
        <ReferenceField label="Lot" source="lotID" reference="lots" link="show">
          <TextField source="name" />
        </ReferenceField>
        <LotVesselLinkField label="Lot Vessel Association ID" />

        <CustomDatetimeField
          source="startTime"
          label="Start Time"
          emptyText={"\u00A0"}
        />
        <CustomDatetimeField
          source="endTime"
          label="End Time"
          emptyText={"\u00A0"}
        />

        <DensityField source="startingDens" label="Starting Density" />
        <BooleanField
          source="startingDensOverridden"
          label="Starting Density Overridden"
        />

        <TextField
          source="mpcTargetBrixRate"
          label="MPC Target Brix Rate (°Bx/Day)"
          emptyText={"\u00A0"}
        />
        <BooleanField source="mpcArmed" label="MPC Armed" />
      </Tab>
    </TabbedShowLayout>
  );
};
